// Click-to-sort over already-fetched rows. The exporter registers had no column
// sort anywhere (BigExport UX audit §5); this is the shared primitive so a table
// only has to map column keys → value getters and wire the header cells.
//
// Sorts a COPY (never mutates the source), blanks always last, numbers compared
// numerically and everything else NATURALLY (compareNatural). Clicking a column
// toggles asc↔desc; clicking a different column starts ascending.
//
// The string branch deliberately does NOT use a bare `localeCompare`: the
// numeric branch only fires when BOTH values are already numbers, so every
// string column that carries digits — block names, pallet refs, bay numbers —
// would otherwise sort lexically (A1, A10, A11 … A2). That is the same defect
// found twice in the irrigation screens; see src/lib/naturalSort.ts.
//
// `accessors` MUST be stable across renders (a module-level const or useMemo) —
// it is intentionally excluded from the memo deps so an inline literal doesn't
// re-sort every render.
//
// A REGISTER whose rows open a report or record keeps its sort in the URL
// (W-21): Back remounts the page, and a sort held in useState came back in the
// default order around the row the operator had returned to. Pass
// `useUrlSort(accessors)` as the fourth argument.
import { useMemo, useState } from 'react'
import { compareNatural } from '@/lib/naturalSort'
import { useUrlState } from '@/hooks/useUrlState'

export type SortDir = 'asc' | 'desc'
export interface SortState { key: string | null; dir: SortDir }
export type Accessors<T> = Record<string, (r: T) => string | number | null | undefined>

/** Sort state held by the caller instead of the hook (e.g. `useUrlSort`). */
export interface SortControl { sort: SortState; setSort: (next: SortState) => void }

const NO_SORT: SortState = { key: null, dir: 'asc' }

export function useTableSort<T>(
  rows: T[],
  accessors: Accessors<T>,
  initial: SortState = NO_SORT,
  control?: SortControl,
) {
  const [own, setOwn] = useState<SortState>(initial)
  const sort = control ? control.sort : own

  const sorted = useMemo(() => {
    const get = sort.key ? accessors[sort.key] : null
    if (!get) return rows
    const mul = sort.dir === 'asc' ? 1 : -1
    // null-last + numeric-vs-string comparator; branchy but trivial, uncovered.
    // fallow-ignore-next-line complexity
    return [...rows].sort((a, b) => {
      const av = get(a), bv = get(b)
      // A blank cell reads the same as a missing one, so '' shares the
      // always-last rule rather than sorting to the top of an ascending column.
      const aBlank = av == null || av === ''
      const bBlank = bv == null || bv === ''
      if (aBlank && bBlank) return 0
      if (aBlank) return 1            // blanks last, regardless of direction
      if (bBlank) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul
      return compareNatural(String(av), String(bv)) * mul
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort])

  const toggle = (key: string) => {
    const next = (s: SortState): SortState =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    if (control) control.setSort(next(control.sort))
    else setOwn(next)
  }

  /** ' ▲' / ' ▼' for the active column, '' otherwise — append to a <th> label. */
  const indicator = (key: string) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  return { sorted, sort, toggle, indicator }
}

/** `?sort=` ↔ SortState: `phi.desc`. A key the table does not have, or a
 *  malformed value, reads as no sort. */
export function sortFromParam(raw: string, keys: readonly string[]): SortState | null {
  const m = /^(.+)\.(asc|desc)$/.exec(raw)
  return m && keys.includes(m[1]) ? { key: m[1], dir: m[2] as SortDir } : null
}
export const sortToParam = (s: SortState): string => (s.key ? `${s.key}.${s.dir}` : '')

/** A table sort that lives in the URL (`?sort=key.dir`, absent = unsorted), so
 *  record → Back keeps the order. Feed it to useTableSort's `control`. */
export function useUrlSort<T>(accessors: Accessors<T>, param = 'sort'): SortControl {
  const keys = Object.keys(accessors)
  const [raw, setRaw] = useUrlState(param, '', r => (sortFromParam(r, keys) ? r : null), { elide: true })
  return { sort: sortFromParam(raw, keys) ?? NO_SORT, setSort: next => setRaw(sortToParam(next)) }
}
